
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('newsletter-subscribe', {
        body: { email },
      });

      if (error) throw error;

      toast.success('Successfully subscribed to our newsletter!');
      setEmail('');
    } catch (error: any) {
      console.error("Newsletter subscription error:", error);
      toast.error(error.message || 'Failed to subscribe. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="bg-ai-dark text-white pt-16 pb-8">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <img 
              src="/lovable-uploads/logo-2026.svg" 
              alt="AI World Congress 2026 - November 25-26, London Europe UK - AI Conference - AI Events 2026" 
              className="h-[50px] w-auto mb-4"
            />
            <h3 className="text-2xl font-bold mb-6">
              AI Conference London <span className="text-purple-500">- AI World Congress 2026</span>
            </h3>
            <p className="text-gray-400 mb-6">
              The world's premier artificial intelligence conference in London UK Europe, bringing together
              industry leaders, researchers, and innovators as one of best AI Events 2026.
            </p>
            <Link to="/register">
              <Button 
                className="bg-gradient-to-r from-[#F97316] to-[#9f1239] hover:opacity-90 text-white px-6 py-2 font-semibold"
              >
                REGISTER NOW
              </Button>
            </Link>
          </div>
          
          <div>
            <h4 className="text-xl font-bold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/#about" className="text-gray-400 hover:text-purple-400 transition-colors">
                  About
                </Link>
              </li>
              <li>
                <Link to="/speakers" className="text-gray-400 hover:text-purple-400 transition-colors">
                  Speakers
                </Link>
              </li>
              <li>
                <Link to="/agenda" className="text-gray-400 hover:text-purple-400 transition-colors">
                  Schedule
                </Link>
              </li>
              <li>
                <Link to="/register" className="text-gray-400 hover:text-purple-400 transition-colors">
                  Tickets
                </Link>
              </li>
              <li>
                <Link to="/venue" className="text-gray-400 hover:text-purple-400 transition-colors">
                  Venue
                </Link>
              </li>
              <li>
                <Link to="/exhibition" className="text-gray-400 hover:text-purple-400 transition-colors">
                  Expo
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-purple-400 transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-xl font-bold mb-6">Venue</h4>
            <p className="text-gray-400 mb-2">The Great Hall</p>
            <p className="text-gray-400 mb-2">Kensington Conference and Events Centre</p>
            <p className="text-gray-400 mb-2">Hornton St, Kensington</p>
            <p className="text-gray-400 mb-2">London W8 7NX</p>
            <Link 
              to="/venue" 
              className="text-purple-400 hover:text-purple-300 transition-colors inline-block mt-2"
            >
              View on map →
            </Link>
          </div>
          
          <div>
            <h4 className="text-xl font-bold mb-6">Stay Updated</h4>
            <p className="text-gray-400 mb-4">
              Subscribe to our newsletter to receive the latest updates about AI World Congress 2026.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-2">
              <Input 
                type="email" 
                placeholder="Your email address" 
                className="bg-white/10 border-gray-700 text-white placeholder:text-gray-500"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                required
              />
              <Button 
                type="submit"
                className="bg-purple-600 hover:bg-purple-700 text-white"
                disabled={isLoading}
              >
                {isLoading ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm mb-4 md:mb-0">
              © 2026 AI Conference London - AI World Congress 2026. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-gray-500 hover:text-purple-400 transition-colors text-sm">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-500 hover:text-purple-400 transition-colors text-sm">
                Terms of Service
              </Link>
              <Link to="/code-of-conduct" className="text-gray-500 hover:text-purple-400 transition-colors text-sm">
                Code of Conduct
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

